import React, { useCallback, useRef, useState } from 'react';

interface CropData {
  x: number; y: number; w: number; h: number;
}

interface CropOverlayProps {
  crop: CropData;
  onApply: (crop: CropData) => void;
  onCancel: () => void;
}

const PRESETS: { label: string; ratio?: number }[] = [
  { label: '9:16', ratio: 9 / 16 },
  { label: '1:1', ratio: 1 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '4:5', ratio: 4 / 5 },
  { label: 'Free' },
];

const CropOverlay: React.FC<CropOverlayProps> = ({ crop, onApply, onCancel }) => {
  const [local, setLocal] = useState<CropData>(crop);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  const applyPreset = useCallback((ratio?: number) => {
    if (!ratio) {
      setLocal({ x: 0, y: 0, w: 1, h: 1 });
      return;
    }
    // Calculate maximum crop that fits the ratio, centered
    // Assuming source is wider than tall (16:9). If ratio > sourceAspect, full width; else full height
    // We work in normalized coords so source is 1x1
    let w: number, h: number;
    if (ratio < 1) {
      // Portrait: height = 1, width = ratio
      h = 1;
      w = Math.min(ratio, 1);
    } else {
      // Landscape: width = 1, height = 1/ratio
      w = 1;
      h = Math.min(1 / ratio, 1);
    }
    setLocal({ x: (1 - w) / 2, y: (1 - h) / 2, w, h });
  }, []);

  const handleCropDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...local };

    const onMouseMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      setLocal({
        ...startCrop,
        x: Math.max(0, Math.min(1 - startCrop.w, startCrop.x + dx)),
        y: Math.max(0, Math.min(1 - startCrop.h, startCrop.y + dy)),
      });
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [local]);

  const handleHandleDrag = useCallback((e: React.MouseEvent, handle: string) => {
    e.stopPropagation();
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...local };
    setDragging(handle);

    const onMouseMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      const c = { ...startCrop };

      if (handle.includes('l')) { c.x = Math.max(0, c.x + dx); c.w = Math.max(0.05, c.w - dx); }
      if (handle.includes('r')) { c.w = Math.max(0.05, Math.min(1 - c.x, c.w + dx)); }
      if (handle.includes('t')) { c.y = Math.max(0, c.y + dy); c.h = Math.max(0.05, c.h - dy); }
      if (handle.includes('b')) { c.h = Math.max(0.05, Math.min(1 - c.y, c.h + dy)); }

      setLocal(c);
    };
    const onMouseUp = () => {
      setDragging(null);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [local]);

  const handles = ['tl', 't', 'tr', 'l', 'r', 'bl', 'b', 'br'];

  return (
    <div className="crop-overlay-wrapper" ref={containerRef}>
      {/* Dark mask */}
      <div className="crop-mask crop-mask-top" style={{ height: `${local.y * 100}%` }} />
      <div className="crop-mask crop-mask-bottom" style={{ height: `${(1 - local.y - local.h) * 100}%` }} />
      <div className="crop-mask crop-mask-left" style={{
        top: `${local.y * 100}%`, height: `${local.h * 100}%`, width: `${local.x * 100}%`,
      }} />
      <div className="crop-mask crop-mask-right" style={{
        top: `${local.y * 100}%`, height: `${local.h * 100}%`, width: `${(1 - local.x - local.w) * 100}%`,
      }} />

      {/* Crop rectangle */}
      <div
        className="crop-rect"
        style={{
          left: `${local.x * 100}%`, top: `${local.y * 100}%`,
          width: `${local.w * 100}%`, height: `${local.h * 100}%`,
        }}
        onMouseDown={handleCropDrag}
      >
        {/* Grid lines */}
        <div className="crop-grid">
          <div className="crop-grid-line crop-grid-h1" />
          <div className="crop-grid-line crop-grid-h2" />
          <div className="crop-grid-line crop-grid-v1" />
          <div className="crop-grid-line crop-grid-v2" />
        </div>

        {/* Handles */}
        {handles.map(h => (
          <div
            key={h}
            className={`crop-handle crop-handle-${h} ${dragging === h ? 'active' : ''}`}
            onMouseDown={(e) => handleHandleDrag(e, h)}
          />
        ))}
      </div>

      {/* Preset bar */}
      <div className="crop-presets">
        {PRESETS.map(p => (
          <button key={p.label} className="btn-sm crop-preset-btn" onClick={() => applyPreset(p.ratio)}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="crop-actions">
        <button className="btn-sm btn-accent" onClick={() => onApply(local)}>Apply</button>
        <button className="btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default CropOverlay;
